import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { MultiSelect, MultiSelectOption } from "@utahdts/utah-design-system";
import { GET_WATER_SYSTEM_RESULTS } from "../../../queries/publicQrys/SystemHeaderViewQrys";

const MultiSelectWaterSystems = ({
  selectedWaterSystems = [],
  existingAssignedSystems = [],
  onChange,
  onAssignedSystemsChange,
  className,
  id = "waterSystems",
  label = "Water Systems",
  placeholder = "Search for a Water System"
}) => {
  const abortControllerRef = useRef(null);
  const systemByLabelRef = useRef({});
  const wrapperRef = useRef(null);
  const [waterSystems, setWaterSystems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchWaterSystems] = useLazyQuery(GET_WATER_SYSTEM_RESULTS);

  const clearSearchState = useCallback(() => {
    setSearchTerm("");
    setWaterSystems([]);
  }, []);

  const createSelectedSystemsObjectsForTransfer = useCallback((selectedValues, lookupMap, existingByName) => (
    (selectedValues || [])
      .map((systemName) => {
        const system = lookupMap[systemName];
        if (system) {
          return { systemId: system.value, systemName: system.systemName || system.label };
        }

        return existingByName[systemName] || null;
      })
      .filter(Boolean)
  ), []);

  const executeSearch = useCallback((searchString) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    searchWaterSystems({
      variables: { searchString },
      context: { signal: controller.signal }
    }).then((result) => {
      const normalizedSystems = (result?.data?.waterSystemSearch || []).map((item) => ({
        value: item.systemId,
        label: `${item.county} / ${item.systemName} / ${item.alternateNum} / ${item.systemType}`,
        systemName: item.systemName
      }));

      normalizedSystems.forEach((system) => {
        systemByLabelRef.current[system.label] = system;
      });

      setWaterSystems(normalizedSystems);
    }).catch((error) => {
      if (error.name !== "AbortError") {
        console.error("Search query error:", error);
      }
    });
  }, [searchWaterSystems]);

  const handleInput = useCallback((e) => {
    setSearchTerm(e.target?.value || "");
  }, [setSearchTerm]);

  useEffect(() => {
    const trimmedSearchTerm = (searchTerm || "").trim();

    if (!trimmedSearchTerm) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      setWaterSystems([]);
      return undefined;
    }

    const timer = setTimeout(() => {
      executeSearch(trimmedSearchTerm);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [executeSearch, searchTerm]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const handleBlur = useCallback(() => {
    // MultiSelect moves focus between internal elements (input/tags/buttons).
    // Only clear text if focus actually left the whole control.
    setTimeout(() => {
      const activeElement = document.activeElement;
      const focusIsInside = !!(wrapperRef.current && activeElement && wrapperRef.current.contains(activeElement));

      if (!focusIsInside && searchTerm && searchTerm.trim().length > 0) {
        clearSearchState();
      }
    }, 0);
  }, [clearSearchState, searchTerm]);

  //quicker lookups when constructing transfer objects
  const systemsByLabel = useMemo(() => {
    const map = { ...systemByLabelRef.current };
    waterSystems.forEach((system) => {
      if (system?.label) {
        map[system.label] = system;
      }
    });
    return map;
  }, [waterSystems]);

  const existingSystemsByName = useMemo(() => {
    const byName = {};
    (existingAssignedSystems || []).forEach((system) => {
      if (system?.systemName && system?.systemId !== undefined && system?.systemId !== null) {
        byName[system.systemName] = {
          systemId: String(system.systemId),
          systemName: system.systemName
        };
      }
    });
    return byName;
  }, [existingAssignedSystems]);

  const handleChange = useCallback((selectedValues) => {
    const safeSelectedValues = Array.isArray(selectedValues) ? selectedValues : [];
    const previousCount = (selectedWaterSystems || []).length;
    const nextCount = safeSelectedValues.length;
    const selectionWasAdded = nextCount > previousCount;

    // Defer parent updates to avoid setState during MultiSelect provider render.
    setTimeout(() => {
      onChange?.(safeSelectedValues);
    }, 0);

    if (selectionWasAdded) {
      clearSearchState();
    }
  }, [clearSearchState, onChange, selectedWaterSystems]);

  const displayWaterSystems = useMemo(() => {
    const byLabel = {};

    waterSystems.forEach((system) => {
      if (system?.label) {
        byLabel[system.label] = system;
      }
    });

    (selectedWaterSystems || []).forEach((selectedLabel) => {
      if (!byLabel[selectedLabel]) {
        byLabel[selectedLabel] = {
          value: selectedLabel,
          label: selectedLabel,
          systemName: selectedLabel
        };
      }
    });

    return Object.values(byLabel);
  }, [selectedWaterSystems, waterSystems]);

  useEffect(() => {
    if (onAssignedSystemsChange) {
      const assignedSystems = createSelectedSystemsObjectsForTransfer(selectedWaterSystems, systemsByLabel, existingSystemsByName);
      onAssignedSystemsChange(assignedSystems);
    }
  }, [onAssignedSystemsChange, selectedWaterSystems, systemsByLabel, existingSystemsByName, createSelectedSystemsObjectsForTransfer]);

  return (
    <MultiSelect
      id={id}
      label={label}
      innerRef={wrapperRef}
      onChange={handleChange}
      onInput={handleInput}
      onBlur={handleBlur}
      value={searchTerm}
      values={selectedWaterSystems}
      className={className}
      placeholder={placeholder}
      autoComplete="off"
    >
      {displayWaterSystems.map((ws) => (
        <MultiSelectOption
          key={ws.value}
          label={ws.label}
          value={ws.label}
        />
      ))}
    </MultiSelect>
  );
};

export default MultiSelectWaterSystems;
